import { useEffect } from 'react'

import { PluginClient } from '@remixproject/plugin'
import { IRemixApi } from '@remixproject/plugin-api'
import { PluginApi } from '@remixproject/plugin-utils'

import { useWeb3ModalProvider } from '@web3modal/ethers/react'
import { BrowserProvider, JsonRpcProvider, JsonRpcSigner, Wallet } from 'ethers'

import Accordion from 'react-bootstrap/Accordion'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'

import { onboardUser } from '../utils/transactions'
import SpinnerButton from './shared/SpinnerButton'
import { useEnvironmentStore } from '../zustand/environmentStore'
import { WalletConfigFileContents } from '../utils/types'

type OnboardProps = {
    client: PluginClient<any, Readonly<IRemixApi>> & PluginApi<Readonly<IRemixApi>>
}

const Onboard = ({ client }: OnboardProps) => {
    const { environment, privateKey, publicKey, rpcUrl, userKey, updateUserKey } = useEnvironmentStore()
    const { walletProvider } = useWeb3ModalProvider()

    useEffect(() => {
        client.onload(async () => {
            try {
                const fileContents = await client.fileManager.getFile('user_aes_key.json')
                updateUserKey(JSON.parse(fileContents)['user_key'])
            } catch (err) {
                // handle case where file does not exist or content is in unexpected format
            }
        })
    }, [])

    const readConfigFile = async () => {
        let fileContents: WalletConfigFileContents
        
        try {
            fileContents = JSON.parse(await client.fileManager.getFile('coti_wallet_config.json'))

            return fileContents
        } catch (e) {
            return
        }
    }

    const handleOnboardUser = async () => {
        let signer: JsonRpcSigner | Wallet

        if (environment === 'Wallet') {
            if (walletProvider === undefined) throw Error('Wallet provider is undefined')
    
            const ethersProvider = new BrowserProvider(walletProvider)

            signer = await ethersProvider.getSigner()
        } else if (environment === 'Manual') {
            const provider = new JsonRpcProvider(rpcUrl)

            signer = new Wallet(privateKey, provider)
        } else {
            throw new Error('Unrecognized environment type')
        }

        const userKey = await onboardUser(client, signer)

        if (userKey === '') {
            return
        }

        updateUserKey(userKey)

        const fileContents = await readConfigFile()

        if (!fileContents) {
            return
        }

        Object.values(fileContents).map((value, index) => {
            if (value.public_key === publicKey) {
                value.user_key = userKey
            }
        })

        await client.fileManager.writeFile('coti_wallet_config.json', JSON.stringify(fileContents, null, "\t"))
    }

    const handleClearKey = async () => {
        const fileContents = await readConfigFile()

        if (!fileContents) {
            return
        }

        Object.values(fileContents).map((value, index) => {
            if (value.public_key === publicKey) {
                delete value.user_key
            }
        })

        await client.fileManager.writeFile('coti_wallet_config.json', JSON.stringify(fileContents, null, "\t"))

        updateUserKey('')
    }

    return (
        <Accordion.Item eventKey='2'>
            <Accordion.Header><b>2 - Onboard</b></Accordion.Header>
            <Accordion.Body className='onboard-container'>
                {userKey === ''
                    ?   <Alert variant='info' className='deployed-contracts-alert'>Click Onboard to generate your AES key</Alert>
                    :   <>
                            <h6>User AES key:</h6>
                            <p>{userKey}</p>
                        </>
                }

                <div className='onboard-btn-container'>
                    <SpinnerButton
                        variant='warning'
                        size='sm'
                        handleFunction={handleOnboardUser}
                        disabled={publicKey === '' || userKey !== ''}
                        text='Onboard'
                    />

                    <Button variant='danger' size='sm' onClick={async () => await handleClearKey()} disabled={userKey === ''}>Clear AES Key</Button>
                </div>
            </Accordion.Body>
        </Accordion.Item>
    )
}

export default Onboard